import { CoreIcon } from "./CoreIcon";
import {
  describeActivityProgress,
  type ActivityConnection,
  type ActivityStreamState,
} from "../../shared/activityStream";

/**
 * The live account of what Document Intelligence is doing.
 *
 * This replaces the plan-approval block and the bare spinner. The complaint it answers
 * is specific: a spinner that said nothing, and a progress display that claimed the work
 * was complete while it was still running.
 *
 * Three properties are load-bearing and are asserted in `ThreadActivityPanel.test.tsx`:
 *
 *   1. Completion wording appears only after the run has actually ended. The headline and
 *      every spinner are driven from `describeActivityProgress`, which will not use it
 *      earlier. There is exactly one status line, so there is nothing for a second one to
 *      contradict.
 *   2. With the toggle OFF, no reasoning content is rendered — not hidden by CSS, not
 *      present in an aria-hidden node. It is not in the tree at all.
 *   3. With the toggle ON, reasoning sits in its own region carrying an explicit warning
 *      that it is not the answer and not advice.
 */

export interface ThreadActivityPanelProps {
  state: ActivityStreamState;
  connection?: ActivityConnection;
  showWorking: boolean;
  onShowWorkingChange?: (next: boolean) => void;
}

const STAGE_ICON: Record<string, "check" | "warning"> = {
  succeeded: "check",
  failed: "warning",
};

export function ThreadActivityPanel({
  state,
  connection = "idle",
  showWorking,
  onShowWorkingChange,
}: ThreadActivityPanelProps) {
  const progress = describeActivityProgress(state, connection);
  // Only ever true when the toggle is on AND lines actually arrived. The toggle being on
  // is checked here, not inside the region, so that "off" cannot render an empty labelled
  // box that implies something is being withheld.
  const showReasoning = showWorking && state.reasoning.length > 0;

  return (
    <section className="di-core-activity" aria-labelledby="di-core-activity-title" data-phase={progress.phase}>
      <div className="di-core-activity__heading">
        <p id="di-core-activity-title" className="di-core-kicker">What Document Intelligence is doing</p>
        <button
          type="button"
          className={`di-core-btn ${showWorking ? "di-core-btn--selected" : "di-core-btn--secondary"} di-core-activity__toggle`}
          aria-pressed={showWorking}
          disabled={!onShowWorkingChange}
          title={onShowWorkingChange
            ? "Raw intermediate reasoning from the model. Not the answer."
            : "This setting is unavailable here."}
          onClick={() => onShowWorkingChange?.(!showWorking)}
        >
          {showWorking ? "Hide the model's working" : "Show the model's working"}
        </button>
      </div>

      {/* Exactly one status line. A second one is how a screen ends up contradicting itself. */}
      <p className="di-core-activity__status" data-testid="di-core-activity-status" role="status" aria-live="polite">
        {progress.isWorking && <span className="di-core-spinner" aria-hidden="true" />}
        <strong>{progress.headline}</strong>
        <span>{progress.detail}</span>
      </p>

      {!!state.stages.length && (
        <ol className="di-core-activity__stages" aria-label="Steps in this request">
          {state.stages.map((stage) => (
            <li key={stage.key} className={`is-${stage.status}`} data-testid={`di-core-activity-stage-${stage.key}`}>
              {stage.status === "running"
                ? <span className="di-core-spinner" aria-hidden="true" />
                : <CoreIcon name={STAGE_ICON[stage.status] || "check"} size={14} aria-hidden="true" />}
              <span>
                {stage.label || stage.key}
                <small>{stage.status === "running" ? "running" : stage.status === "failed" ? "failed" : "done"}</small>
              </span>
            </li>
          ))}
        </ol>
      )}

      <ol className="di-core-activity__log" aria-label="Live activity" aria-live="polite" data-testid="di-core-activity-log">
        {state.activity.map((line) => (
          <li key={line.id}>{line.text}</li>
        ))}
        {!state.activity.length && (
          <li className="di-core-activity__log-empty">
            {progress.isWorking
              ? "Waiting for the first update from this run."
              : "Nothing has been reported for this request."}
          </li>
        )}
      </ol>

      {showReasoning && (
        <section
          className="di-core-working"
          aria-labelledby="di-core-working-title"
          data-testid="di-core-working"
        >
          <p id="di-core-working-title" className="di-core-working__label">
            <CoreIcon name="warning" size={14} aria-hidden="true" />
            The model&apos;s working-out — not the answer, and not advice
          </p>
          <p className="di-core-working__caution">
            This is the model&apos;s raw, unedited internal reasoning while it works. It routinely
            contains guesses, dead ends, and statements the model itself goes on to reject. It has
            not been checked against your documents and nothing in it is cited. It is shown so you
            can see how the work proceeded. Do not rely on it, quote it, or treat any part of it as
            the answer or as legal advice — the answer is the response below.
          </p>
          <ol className="di-core-working__lines">
            {state.reasoning.map((line) => (
              <li key={line.id}>{line.text}</li>
            ))}
          </ol>
          <p className="di-core-working__label di-core-working__label--end">
            End of the model&apos;s working-out. The answer follows.
          </p>
        </section>
      )}
    </section>
  );
}
